import type { JSX, ReactNode } from 'react';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import type { RecurringItemTaxCodeDto } from '@pflegenavi/shared/api';
import { TaxCodePicker, formatTaxCodeLabel } from './TaxCodePicker';
import type { NestedKeysToObject } from './FTextInput';

interface FTaxCodePickerProps<T extends string, V> {
  name: T;
  formik: FormikProps<V>;
  taxCodes: RecurringItemTaxCodeDto[];
  label: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  allowNone?: boolean;
  noneLabel?: ReactNode;
  id?: string;
}

export const FTaxCodePicker = <
  FieldName extends string,
  V extends NestedKeysToObject<FieldName>
>({
  name,
  formik,
  disabled,
  ...rest
}: FTaxCodePickerProps<FieldName, V>): JSX.Element => {
  const fieldValue =
    (getIn(formik.values, name) as string | null | undefined) ?? null;
  const fieldError = getIn(formik.errors, name);
  const fieldTouched = getIn(formik.touched, name);
  const showError = Boolean(fieldTouched && typeof fieldError === 'string');

  return (
    <TaxCodePicker
      {...rest}
      name={name}
      value={fieldValue}
      getOptionLabel={formatTaxCodeLabel}
      onChange={(tc) => {
        formik.setFieldValue(name, tc?.id ?? null);
      }}
      error={showError}
      helperText={showError ? fieldError : undefined}
      disabled={disabled || formik.isSubmitting}
    />
  );
};
